import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { DocumentCheckIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { loadStripe } from '@stripe/stripe-js';

// Components
import Button from '../../../components/ui/Button';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import MerchantFundCollection from '../../../components/dashboard/returns/MerchantFundCollection';
import {
  ReturnSummaryInfo,
  DecisionControls,
  ReturnItemRow,
  RefundCalculationDisplay
} from '../../../components/dashboard/returns';

// Redux/API
import { useGetFulfillmentGroupDetailsQuery } from '../../../redux/apis/endpoints/orderApi';

// Hooks and Utils
import { useReturnProcessing } from './hooks/useReturnProcessing';
import { extractGroupReturnReason, calculateHeldDuration, filterPendingReturnItems } from './utils/returnItemUtils';
import { formatCurrency } from '../../../utils/formatters';

// Get stripe key from environment
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const ProcessReturnPage = () => {
  const { fulfillmentGroupId } = useParams();
  const history = useHistory();
  
  // Add debugging
  console.log('ProcessReturnPage - fulfillmentGroupId from useParams:', fulfillmentGroupId);
  
  // RTK Query hooks
  const { 
    data: entity,
    isLoading: isFetchingFG,
    error: fetchError
  } = useGetFulfillmentGroupDetailsQuery(fulfillmentGroupId);
  
  // Derived state
  const pendingReturnItems = React.useMemo(() => filterPendingReturnItems(entity), [entity]);
  
  // Custom hook for all return processing logic
  const {
    // State
    decision,
    rejectionReason,
    usageFee,
    selectedReturnAddress,
    refundFundPaymentCompleted,
    estimatedReturnShipping,
    isLoadingShipping,
    
    // Derived state
    currency,
    anyLoading,
    calculatedRefund,
    refundAmountToPay,
    
    // Data
    merchantAddresses,
    isLoadingAddresses,
    
    // Handlers
    handleAddressChange,
    handleAddressSuccess,
    handleDecisionChange,
    executeDecision,
    handleFundCollectionSuccess,
    handleFundCollectionError,
    
    // Setters for components that need direct control
    setRejectionReason,
    setUsageFee,
    setCurrentPaymentIntent
  } = useReturnProcessing(fulfillmentGroupId, entity, pendingReturnItems);
  
  // Calculate display values
  const groupReturnReason = React.useMemo(() => extractGroupReturnReason(pendingReturnItems), [pendingReturnItems]);
  const fgHeldDisplay = React.useMemo(() => calculateHeldDuration(entity, pendingReturnItems), [entity, pendingReturnItems]);
  
  // Show loading state
  if (isFetchingFG) {
    return <div className="flex justify-center items-center h-64">
      <LoadingSpinner />
    </div>;
  }
  
  // Show error state
  if (fetchError) {
    return <div className="max-w-4xl mx-auto p-4 bg-red-50 text-red-700 rounded-lg">
      <h2 className="text-lg font-medium mb-2">Error Loading Return Data</h2>
      <p>{fetchError.message || 'Unable to load fulfillment group details'}</p>
      <Button
        variant="secondary"
        className="mt-4"
        onClick={() => history.goBack()}
      >
        Go Back
      </Button>
    </div>;
  }
  
  // Render the return processing page
  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex items-center mb-6">
        <DocumentCheckIcon className="h-12 w-12 text-blue-500 mr-4" />
        <div>
          <h1 className="text-2xl font-medium text-gray-900">Process Return Requests</h1>
          <p className="text-sm text-gray-600 mt-1">
            Approve or reject the return request for this fulfillment group.
          </p>
        </div>
      </div>

      {pendingReturnItems.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-lg shadow-sm border p-8">
          <p className="text-gray-500">No pending return requests in this group.</p>
          <Button
            variant="secondary"
            className="mt-4"
            onClick={() => history.goBack()}
          >
            Go Back
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Items List */}
          <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Return Items</h2>
            <div className="max-h-[50vh] overflow-y-auto space-y-4 mb-4">
              {pendingReturnItems.map(item => (
                <div className="border border-gray-200 rounded-lg p-4" key={item.id}>
                  <ReturnItemRow item={item} />
                </div>
              ))}
            </div>
            {/* Group Return Reason & Held Duration */}
            <ReturnSummaryInfo 
              groupReturnReason={groupReturnReason}
              fgHeldDisplay={fgHeldDisplay}
            />
          </div>

           {/* Decision Controls */}
           <DecisionControls 
            decision={decision}
            rejectionReason={rejectionReason}
            usageFee={usageFee}
            calculatedRefund={calculatedRefund}
            currency={currency}
            pendingReturnItems={pendingReturnItems}
            refundFundPaymentCompleted={refundFundPaymentCompleted}
            refundAmountToPay={refundAmountToPay}
            onDecisionChange={handleDecisionChange}
            onRejectionReasonChange={(e) => setRejectionReason(e.target.value)}
            onUsageFeeChange={(e) => setUsageFee(e.target.value)}
          />
          
          {/* Refund Calculation Display - only show when approving */}
          {decision === 'approve' && (
            <RefundCalculationDisplay 
              pendingReturnItems={pendingReturnItems}
              usageFee={usageFee}
              estimatedReturnShipping={estimatedReturnShipping}
              currency={currency}
              calculatedRefund={calculatedRefund}
              isLoadingShipping={isLoadingShipping}
              selectedReturnAddress={selectedReturnAddress}
              merchantAddresses={merchantAddresses}
              isLoadingAddresses={isLoadingAddresses}
              onAddressChange={handleAddressChange}
              onAddressSuccess={handleAddressSuccess}
            />
          )}

          {/* Fund Collection Section (show when approving and payment not completed yet) */}
          {decision === 'approve' && !refundFundPaymentCompleted && refundAmountToPay > 0 && (
            <MerchantFundCollection
              stripePromise={stripePromise}
              refundAmount={refundAmountToPay}
              currency={currency}
              fulfillmentGroupId={fulfillmentGroupId}
              usageFee={usageFee}
              onPaymentSuccess={handleFundCollectionSuccess}
              onPaymentError={handleFundCollectionError}
              show={true}
            />
          )}
          
          {/* Success message when payment is completed */}
          {decision === 'approve' && refundFundPaymentCompleted && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                <div className="text-sm text-green-800">
                  <p className="font-medium">Refund Payment Completed</p>
                  <p>Payment of {formatCurrency(refundAmountToPay, currency)} processed successfully. You can now approve the return request below.</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Payment status for reject decision */}
          {decision === 'reject' && refundFundPaymentCompleted && refundAmountToPay > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mt-0.5 mr-2" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Refund Payment Will Be Returned</p>
                  <p>You have already paid {formatCurrency(refundAmountToPay, currency)} for this return request. If you reject this return, this amount will be automatically refunded to you.</p>
                </div>
              </div>
            </div>
          )}
          
         

          {/* Footer Buttons */}
          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 mt-6">
            <Button
              variant="secondary"
              onClick={() => history.goBack()}
              disabled={anyLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={executeDecision}
              disabled={
                anyLoading || 
                pendingReturnItems.length === 0 || 
                (decision === 'approve' && !selectedReturnAddress && merchantAddresses.length > 0) ||
                (decision === 'approve' && calculatedRefund < 0) ||
                (decision === 'approve' && !refundFundPaymentCompleted && refundAmountToPay > 0)
              }
              isLoading={anyLoading}
              className="flex items-center justify-center"
            >
              {decision === 'approve' && calculatedRefund < 0
                ? 'Customer Refund Cannot Be Negative'
                : decision === 'approve' && !refundFundPaymentCompleted && refundAmountToPay > 0
                  ? 'Complete Payment Above to Proceed' 
                  : decision === 'reject' && refundFundPaymentCompleted && refundAmountToPay > 0
                    ? `Reject & Refund ${formatCurrency(refundAmountToPay, currency)}`
                    : decision === 'approve'
                      ? 'Approve Return Request'
                      : 'Reject Return Request'
              }
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessReturnPage; 